import React, { useEffect, useState } from 'react';
import { Box, Typography, Alert, Checkbox, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { getSurveyRetro, trackRetroAction, generateRetroThemes } from '../../../services/surveyService';
import { setCeremonies, saveRetroActions } from '../../../services/sprintHealthService';
import { A, APill, ABtn, Seg, Panel, Cap, Mark, RagChip } from './reviewUi';
import { pulseTone, retroRag } from './rag';

// Same tints and icons as the public survey page, so the boxes read the same in both places.
const BOXES = [
  { id: 'start', label: 'Start', bg: '#E3F1E6', fg: '#1F4D2B', icon: <path d="M12 5v14M5 12h14" /> },
  { id: 'stop', label: 'Stop', bg: '#FBE6E4', fg: '#8A1F18', icon: <rect x="6" y="6" width="12" height="12" rx="2" /> },
  { id: 'continue', label: 'Continue', bg: '#E2EDF8', fg: '#174A80', icon: <path d="M4 12a8 8 0 0 1 14-5.3L20 9M20 4v5h-5M20 12a8 8 0 0 1-14 5.3L4 15M4 20v-5h5" /> },
];
const TONE_COLOR = { r: A.bad, a: '#e08a00', g: A.good, n: A.line };
const SHOWN = 3;

const Icon = ({ children }) => (
  <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const sameText = (a, b) => a.trim().toLowerCase() === b.trim().toLowerCase();

// Tint for each answer value, 1 (strongly disagree) to 5 (strongly agree), by the pulse rule.
const VALUE_TONE = ['r', 'r', 'a', 'g', 'g'];
const TONE_SOFT = { r: A.badSoft, a: '#FFF1D6', g: A.goodSoft };
const TONE_INK = { r: A.bad, a: '#8A5A00', g: A.good };

/** The 1 to 5 scale for one question: a value is tinted when someone picked it; hover shows how many did. */
const Spread = ({ distribution, dense }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 0.5, mt: 0.75, maxWidth: dense ? 200 : 'none' }} aria-label={distribution.map((n, i) => `${i + 1}: ${n} ${n === 1 ? 'person' : 'people'}`).join(', ')}>
    {distribution.map((n, i) => {
      const tone = VALUE_TONE[i];
      return (
        <Box key={i} component="span" aria-hidden="true" title={`${n} ${n === 1 ? 'person' : 'people'} picked ${i + 1}`} sx={{ py: 0.15, borderRadius: '6px', textAlign: 'center', fontSize: dense ? 11 : 11.5, bgcolor: n ? TONE_SOFT[tone] : A.soft, color: n ? TONE_INK[tone] : A.ink3, fontWeight: n ? 700 : 500 }}>
          {i + 1}
        </Box>
      );
    })}
  </Box>
);

/**
 * Each survey question: how many answered (after the title), its average (bar coloured by the pulse
 * rule, lowest marked) and the 1 to 5 scale. `cards` lays the questions out as a 3-across grid of
 * small cards. A sprint without a survey shows the older check-in pulse, when it has one.
 */
export const SurveyScores = ({ c, survey, dense = false, cards = false, columns = 1 }) => {
  const sum = survey?.summary;
  if (sum?.hidden) return <Cap>No responses yet. Send the survey link to the team.</Cap>;
  const fromSurvey = !!sum;
  const rows = fromSurvey
    ? sum.questions.map((q) => ({ id: q.id, label: q.title, value: q.mean, distribution: q.distribution, answered: (q.distribution || []).reduce((t, n) => t + n, 0) }))
    : c?.pulse
      ? [['confidence', 'Confident we can meet the goal'], ['pace', 'Pace is sustainable'], ['clarity', 'Priorities are clear']].map(([id, label]) => ({ id, label, value: c.pulse[id] }))
      : [];
  if (!rows.length) return dense ? null : <Cap>No scores yet. Start the survey and send the link to the team.</Cap>;
  const scored = rows.filter((r) => r.value != null);
  const lowest = fromSurvey && scored.length > 1 ? scored.reduce((m, r) => (r.value < m.value ? r : m)) : null;
  const card = cards ? { border: `1px solid ${A.line}`, borderRadius: '10px', p: '12px 14px' } : {};
  return (
    <Box sx={{ display: 'grid', gap: cards ? 1.5 : dense ? 1 : 1.5, columnGap: columns > 1 && !cards ? 4 : undefined, gridTemplateColumns: cards ? { xs: 'minmax(0,1fr)', sm: 'repeat(2, minmax(0,1fr))', ...(columns === 2 ? {} : { xl: 'repeat(3, minmax(0,1fr))' }) } : columns > 1 ? { xs: 'minmax(0,1fr)', md: `repeat(${columns}, minmax(0,1fr))` } : 'minmax(0,1fr)' }}>
      {rows.map((r) => (
        <Box key={r.id} sx={card}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1.25, fontSize: dense ? 12.5 : 13.5, color: A.ink }}>
            <Box component="span" sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', minWidth: 0, fontWeight: cards ? 500 : 400 }}>
              <span>{r.label}{r.answered != null && <Box component="span" sx={{ color: A.ink3, fontWeight: 400 }} title={`${r.answered} ${r.answered === 1 ? 'person' : 'people'} answered`}> ({r.answered})</Box>}</span>
              {lowest?.id === r.id && <APill tone="warn">Lowest</APill>}
            </Box>
            <Box component="span" sx={cards ? { fontSize: 20, fontWeight: 300 } : { fontWeight: 700 }}>{r.value != null ? r.value.toFixed(1) : '—'}</Box>
          </Box>
          <Box sx={{ height: dense ? 4 : 6, bgcolor: A.soft, borderRadius: 9, mt: 0.4, overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: `${((r.value || 0) / 5) * 100}%`, bgcolor: TONE_COLOR[pulseTone(r.value)], borderRadius: 9 }} />
          </Box>
          {r.distribution && <Spread distribution={r.distribution} dense={dense} />}
        </Box>
      ))}
    </Box>
  );
};

const HELD = { true: 'yes', false: 'no' };

/** Whether the retro was held, set inline; saving it records the retro for the sprint. */
export const RetroHeldToggle = ({ c, team, sprint, editable, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async (v) => {
    setBusy(true);
    setError('');
    try { await setCeremonies(sprint, team, { retroHeld: v === 'yes' }); await onChanged(); } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  return (
    <Box>
      {editable ? (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography component="h3" sx={{ fontSize: 14, fontWeight: 600, color: A.ink }}>Retro held</Typography>
          <Seg value={HELD[c.practice.retroHeld] ?? null} options={[['yes', 'Yes'], ['no', 'No']]} onChange={save} ariaLabel="Retro held" disabled={busy} />
        </Box>
      ) : (
        <Mark state={c.practice.retroHeld}>Retro held</Mark>
      )}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    </Box>
  );
};

const ColumnShell = ({ children, sx }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, bgcolor: A.surface, border: `1px solid ${A.line}`, borderRadius: '12px', overflow: 'hidden', ...sx }}>{children}</Box>
);

/** One of Start / Stop / Continue: AI themes (when made), then the answers with Track. */
const RetroBoxColumn = ({ box, retro, loading, themes, canTrack, tracked, busyText, onTrack }) => {
  const [all, setAll] = useState(false);
  const answers = retro && !retro.hidden ? retro[box.id] : [];
  const list = all ? answers : answers.slice(0, SHOWN);
  const boxThemes = themes?.[box.id] || [];
  const isTracked = (text) => tracked.some((t) => sameText(t, text));
  return (
    <ColumnShell>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, bgcolor: box.bg, color: box.fg }}>
        <Icon>{box.icon}</Icon>
        <Typography component="h3" sx={{ fontSize: 14.5, fontWeight: 600, color: 'inherit', flex: 1 }}>{box.label}</Typography>
        {retro && !retro.hidden && <Box component="span" sx={{ fontSize: 12.5, fontWeight: 600 }}>{answers.length}</Box>}
      </Box>
      <Box sx={{ p: 2, display: 'grid', gap: 1.5, alignContent: 'start', flex: 1 }}>
        {loading && <Cap>Loading…</Cap>}
        {!retro && !loading && <Cap>No survey yet.</Cap>}
        {retro?.hidden && <Cap>No responses yet.</Cap>}
        {retro && !retro.hidden && answers.length === 0 && <Cap>Nothing written.</Cap>}

        {boxThemes.length > 0 && (
          <Box component="ul" aria-label={`${box.label} themes`} sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.75 }}>
            {boxThemes.map((t) => (
              <Box component="li" key={t.theme} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 1.25, py: 0.75, borderRadius: '8px', bgcolor: A.soft, fontSize: 13.5, fontWeight: 500, color: A.ink }}>
                <span>{t.theme}</span>
                <Box component="span" title={`${t.count} ${t.count === 1 ? 'answer' : 'answers'}`} sx={{ flexShrink: 0, minWidth: 24, textAlign: 'center', px: 0.75, borderRadius: 999, bgcolor: box.bg, color: box.fg, fontSize: 12, fontWeight: 700 }}>{t.count}</Box>
              </Box>
            ))}
          </Box>
        )}

        {list.length > 0 && (
          <Box component="ul" aria-label={`${box.label} answers`} sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1 }}>
            {list.map((text, k) => (
              <Box component="li" key={k} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', justifyContent: 'space-between', pt: k ? 1 : 0, borderTop: k ? `1px solid ${A.line}` : 'none' }}>
                <Box component="span" sx={{ fontSize: 13.5, lineHeight: 1.5, color: A.ink2, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{text}</Box>
                {canTrack && (isTracked(text)
                  ? <APill tone="ok">Tracked</APill>
                  : <ABtn sx={{ height: 28, px: 1.25, fontSize: 12.5, flexShrink: 0 }} disabled={busyText === text} onClick={() => onTrack(text)}>Track</ABtn>)}
              </Box>
            ))}
          </Box>
        )}
        {answers.length > SHOWN && (
          <ABtn variant="ghost" sx={{ justifySelf: 'start', height: 30, px: 1, fontSize: 13 }} aria-expanded={all} onClick={() => setAll((v) => !v)}>
            {all ? 'Show fewer' : `Show all ${answers.length}`}
          </ABtn>
        )}
      </Box>
    </ColumnShell>
  );
};

const addedDay = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/** The sprint's retro actions (carried ones included) with the date each was added: tick done, add, remove. */
const RetroActionsColumn = ({ c, team, sprint, editable, onChanged, panel = false }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const actions = c.retro.actions || [];
  const save = async (next) => {
    setBusy(true);
    setError('');
    try { await saveRetroActions(sprint, team, next.map((a) => ({ text: a.text, done: !!a.done, addedAt: a.addedAt || null }))); await onChanged(); return true; } catch (err) { setError(err.message); return false; } finally { setBusy(false); }
  };
  const add = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    if (actions.some((a) => sameText(a.text, t))) { setError('That action is already tracked'); return; }
    if (await save([...actions, { text: t, done: false, addedAt: new Date().toISOString() }])) setText('');
  };
  return (
    <ColumnShell sx={panel ? { borderRadius: '14px', flex: 1 } : undefined}>
      <Box sx={panel
        ? { display: 'flex', alignItems: 'center', gap: 1, px: { xs: 2, md: 3 }, pt: 2.75, pb: 0.5 }
        : { display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, bgcolor: A.sageSoft, color: A.sageInk }}
      >
        <Typography component={panel ? 'h2' : 'h3'} sx={{ fontSize: panel ? 16 : 14.5, fontWeight: 600, color: panel ? A.ink : 'inherit', flex: 1 }}>Retro actions</Typography>
        <Box component="span" sx={{ fontSize: 12.5, fontWeight: 600, color: panel ? A.sageInk : 'inherit' }}>{c.retro.closed} of {c.retro.committed} closed</Box>
        <RagChip rag={retroRag(c)} />
      </Box>
      <Box sx={{ p: panel ? { xs: '12px 16px 20px', md: '12px 24px 20px' } : 2, display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
        {actions.length === 0 && <Cap>None yet. Track a line from the survey or add one below.</Cap>}
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.5 }}>
          {actions.map((a, k) => (
            <Box component="li" key={`${a.text}-${k}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
              {editable ? (
                <Checkbox
                  size="small"
                  checked={!!a.done}
                  disabled={busy}
                  onChange={(e) => save(actions.map((x, i) => (i === k ? { ...x, done: e.target.checked } : x)))}
                  inputProps={{ 'aria-label': `${a.done ? 'Reopen' : 'Mark done'}: ${a.text}` }}
                  sx={{ p: 0.5, mt: '-2px', color: A.ink3, '&.Mui-checked': { color: A.good } }}
                />
              ) : <Box sx={{ pt: 0.25 }}><Mark state={a.done ? true : null} /></Box>}
              <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
                <Box sx={{ fontSize: 13.5, lineHeight: 1.45, color: a.done ? A.ink3 : A.ink, textDecoration: a.done ? 'line-through' : 'none', overflowWrap: 'anywhere' }}>{a.text}</Box>
                {(a.addedAt || a.carriedFrom) && (
                  <Cap>{[a.addedAt && `Added ${addedDay(a.addedAt)}`, a.carriedFrom && `${a.addedAt ? 'open' : 'Open'} since ${a.carriedFrom}`].filter(Boolean).join(' · ')}</Cap>
                )}
              </Box>
              {editable && !a.carriedFrom && (
                <IconButton size="small" aria-label={`Remove ${a.text}`} disabled={busy} onClick={() => save(actions.filter((_, i) => i !== k))}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
        {editable && (
          <Box component="form" onSubmit={add} sx={{ display: 'flex', gap: 1, mt: 'auto', pt: 0.5 }}>
            <Box
              component="input"
              value={text}
              maxLength={300}
              onChange={(e) => setText(e.target.value)}
              placeholder="Add an action"
              aria-label="New retro action"
              sx={{ flex: 1, minWidth: 0, height: 36, px: 1.25, borderRadius: '8px', border: `1px solid ${A.line}`, font: 'inherit', fontSize: 13.5, color: A.ink, '&:focus': { outline: `2px solid ${A.sage}`, outlineOffset: 1 } }}
            />
            <ABtn type="submit" sx={{ height: 36 }} disabled={busy || !text.trim()}>Add</ABtn>
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
      </Box>
    </ColumnShell>
  );
};

/** Retro actions as their own card (layout B's team health column, under Practice). */
export const RetroActionsPanel = (props) => <RetroActionsColumn {...props} panel />;

/**
 * Retro board: Start, Stop and Continue from the anonymous survey beside the sprint's retro
 * actions. Survey text needs a sign-in.
 */
export const RetroBoard = ({ c, data, team, sprint, loggedIn, editable, onChanged, flash, withActions = true }) => {
  const survey = data.survey;
  const roundId = survey?.roundId;
  const count = survey?.summary?.responseCount;
  const [retro, setRetro] = useState(null);
  const [error, setError] = useState('');
  const [busyText, setBusyText] = useState(null);
  const [theming, setTheming] = useState(false);

  useEffect(() => {
    setRetro(null);
    if (!roundId || !loggedIn) return undefined;
    let alive = true;
    setError('');
    getSurveyRetro(roundId).then((d) => { if (alive) setRetro(d); }).catch((err) => { if (alive) setError(err.message); });
    return () => { alive = false; };
  }, [roundId, count, loggedIn]);

  const track = async (text) => {
    setBusyText(text);
    setError('');
    try { await trackRetroAction(sprint, team, text); await onChanged(); } catch (err) { setError(err.message); } finally { setBusyText(null); }
  };
  const makeThemes = async () => {
    setTheming(true);
    setError('');
    try { const themes = await generateRetroThemes(roundId); setRetro((r) => ({ ...r, themes })); } catch (err) { setError(err.message); } finally { setTheming(false); }
  };

  const hasText = retro && !retro.hidden && BOXES.some((b) => retro[b.id].length);
  const themes = retro?.themes;
  const stale = themes && themes.responseCount !== retro.responseCount;
  const tracked = (c.retro.actions || []).map((a) => a.text);
  let caption = 'No survey this sprint. Start one from Sprint retro.';
  if (roundId && !loggedIn) caption = 'Sign in to see what the team wrote.';
  else if (retro?.hidden) caption = 'No responses yet. Send the survey link to the team.';
  else if (retro) caption = `From the anonymous survey · ${retro.responseCount} responses. Included word for word in the leadership email.`;

  return (
    <Panel id="sr-retro" sx={{ scrollMarginTop: 72, transition: 'box-shadow .2s', boxShadow: flash ? `0 0 0 2px ${A.btn}` : 'none', p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h2" sx={{ fontSize: 16, fontWeight: 600, color: A.ink }}>Start, stop, continue</Typography>
          <Cap sx={{ fontSize: 13 }}>{caption}</Cap>
        </Box>
        {hasText && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            {themes && <Cap>{stale ? `Themes from ${themes.responseCount} responses, refresh for the latest` : 'Themes grouped by AI'}</Cap>}
            <ABtn disabled={theming} onClick={makeThemes}>{theming ? 'Grouping…' : themes ? 'Refresh themes' : 'Summarise themes'}</ABtn>
          </Box>
        )}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'grid', gridTemplateColumns: withActions ? { xs: 'minmax(0,1fr)', sm: 'repeat(2, minmax(0,1fr))', lg: 'repeat(4, minmax(0,1fr))' } : { xs: 'minmax(0,1fr)', md: 'repeat(3, minmax(0,1fr))' }, gap: 2, alignItems: 'stretch' }}>
        {BOXES.map((box) => (
          <RetroBoxColumn
            key={box.id}
            box={box}
            retro={roundId && loggedIn ? retro : null}
            loading={!!roundId && loggedIn && !retro && !error}
            themes={stale ? null : themes}
            canTrack={editable}
            tracked={tracked}
            busyText={busyText}
            onTrack={track}
          />
        ))}
        {withActions && <RetroActionsColumn c={c} team={team} sprint={sprint} editable={editable} onChanged={onChanged} />}
      </Box>
    </Panel>
  );
};
